import { AuthFailureError, LogLevel } from '@magicdoorinc/auth';
import { FileUrlVariant, PublicUrlAdapter } from '@magicdoorinc/file';
import { createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import {
  auth,
  redirectToAuth,
  switchToAccount,
  getSignedInAccountIds,
  getSignedInAccounts,
  getActiveAccountId,
  demoteActiveAccountForHandoff,
  signOutAllAccounts,
} from '~/config/auth';
import { createMagicDoorContext } from '~/contexts/utils';
import { companyRepository } from '~/repositories/companiesRepository';
import { MeRepository } from '~/repositories/meRepository';
import { LoginType } from '~/utils/constant';
import { clearThisHostAccounts, serializeThisHostRoster } from '~/utils/crossHostAccounts';
import { fileService } from '~/utils/file';
import { redirectToBusinessModeHostOnLogin } from '~/utils/hoaDemoMode';
import { displayName } from '~/utils/strings';
import type { PropertyManagerDto } from '~/swagger/portal/Api';

export type Profile = {
  id: string;
  name: string;
  firstName: string;
  lastName?: string;
  jobTitle?: string;
  email: string;
  phone?: string;
  address?: string;
  memo?: string;
  avatar: string;
  keywords?: string;
  createdAt: string;
};

const meRepo = new MeRepository();

const COMPANIES_FETCH_ATTEMPTS = 3;
const COMPANIES_RETRY_DELAY_MS = 300;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchCompaniesWithRetry = async () => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await companyRepository.getCompanies();
    } catch (error) {
      const isLastAttempt = attempt === COMPANIES_FETCH_ATTEMPTS;
      if (isLastAttempt || error instanceof AuthFailureError) {
        throw error;
      }
      await delay(COMPANIES_RETRY_DELAY_MS * attempt);
    }
  }
};

const restoreCompanyIdFromToken = () => {
  const activeAccountId = getActiveAccountId();
  if (!activeAccountId) {
    return;
  }

  const companyId = auth.storage.getAccountInfo(activeAccountId)?.company_id;
  if (companyId) {
    localStorage.setItem('companiesId', companyId);
  }
};

const dto2Profile = async (dto: PropertyManagerDto): Promise<Profile> => {
  const avatarUrl = dto.imageId
    ? await fileService.buildFileUrl(new PublicUrlAdapter({ reference: dto.imageId }), FileUrlVariant.Thumb)
    : undefined;

  return {
    name: dto.name ?? displayName(dto.firstName, dto.lastName),
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
    phone: dto.phone,
    avatar: avatarUrl?.url ?? '',
    id: dto.id,
    createdAt: dto.created,
  };
};

export const [AuthProvider, useAuth] = createMagicDoorContext('Auth', () => {
  const [loginType, setLoginType] = createSignal<LoginType>(LoginType.Password);

  const [me, { fetch, refetch, mutate }] = createLazyResource<Profile>(async () => {
    const data = await meRepo.getProfile();
    return await dto2Profile(data);
  });

  const updateProfile = createMutation(async (profile: Profile) => {
    const result = await meRepo.updateProfile(profile);
    mutate(await dto2Profile(result));
  });

  const updateAvatar = createMutation(async (avatar: File) => {
    const result = await meRepo.updateProfileImage(avatar);
    mutate(await dto2Profile(result));
  });

  const changePassword = createMutation(async (currentPassword: string, newPassword: string) => {
    const result = await meRepo.changePassword({ currentPassword, clearTextPassword: newPassword });
    mutate(await dto2Profile(result));
  });

  const signOut = async () => {
    clearThisHostAccounts();
    redirectToAuth(undefined, { reason: null, logReason: 'user_signout' });
  };

  const signOutAll = async () => {
    clearThisHostAccounts();
    await signOutAllAccounts();
    redirectToAuth('/', { reason: null, logReason: 'user_signout_all' });
  };

  const [activeAccountId, setActiveAccountId] = createSignal<string | null>(getActiveAccountId());

  /**
   * Switch the current tab to another signed-in account (login+company).
   * - Instant if that account is already signed in on this device.
   * - Otherwise redirects through a fresh sign-in.
   * Refetches the profile after an in-place switch.
   */
  const switchToAccountAction = createMutation(async (targetAccountId: string) => {
    const result = await switchToAccount(targetAccountId);
    setActiveAccountId(getActiveAccountId());
    mutate(undefined);
    await refetch();
    return result;
  });

  /** Composite ids of accounts already signed in on this device. */
  const signedInAccountIds = () => getSignedInAccountIds();

  const initAndStoreCompanies = async () => {
    let path = auth.getRedirect() ?? '/';

    try {
      const companies = await fetchCompaniesWithRetry();
      const localCompanyId = localStorage.getItem('companiesId');
      if (localCompanyId !== companies.id) {
        path = '/';
      }
      localStorage.setItem('companiesId', companies.id);
      // If the just-logged-in company belongs to the other portal host (HOA vs
      // Company), hand the session off to that host (mint a fresh interaction
      // code so it can complete OAuth silently). A matching company logs in
      // normally below.
      const redirectingToOtherHost = await redirectToBusinessModeHostOnLogin(
        companies.businessMode,
        path,
        async () => {
          const result = await auth.getInteractionToken();
          if (!result.interactionCode) throw new Error('No interaction code returned');
          return result.interactionCode;
        },
        serializeThisHostRoster(getSignedInAccounts())
      );
      // Handing this account off to the sibling host: don't leave it active on THIS
      // origin, or a later visit here would show the wrong portal's account.
      if (redirectingToOtherHost) {
        demoteActiveAccountForHandoff();
        return;
      }
    } catch (error) {
      if (error instanceof AuthFailureError) {
        throw error;
      }

      auth.logger.log(LogLevel.ERROR, 'companies_init_failed', {
        data: { message: error instanceof Error ? error.message : String(error) },
      });

      restoreCompanyIdFromToken();
      path = '/';
    }

    window.location.replace(path);
  };

  return {
    get me() {
      fetch();
      return me;
    },
    refetch,
    updateProfile,
    updateAvatar,
    changePassword,
    signOut,
    signOutAll,
    initAndStoreCompanies,
    loginType,
    setLoginType,
    switchToAccount: switchToAccountAction,
    activeAccountId,
    signedInAccountIds,
  };
});
